class StickyHeader extends HTMLElement {
  setHeightVariable = () =>
    document.documentElement.style.setProperty(
      "--header-height",
      `${this.getBoundingClientRect().height}px`,
    );

  connectedCallback() {
    this.sticking = false;

    window.requestAnimationFrame(this.setHeightVariable);
    window.addEventListener("resize", this.setHeightVariable);

    this._moveStickTarget();
    this._moveQuickLinks();
    this._initObserver();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.setHeightVariable);
    this.observer?.disconnect();
  }

  _initObserver() {
    const cb = (entries) => {
      entries.forEach((entry) => {
        switch (entry.target.dataset.headerTarget) {
          case "stick":
            this.sticking = !entry.isIntersecting;
            this.classList.toggle("sticking", this.sticking);
            document.body.classList.toggle("header--sticking", this.sticking);
            break;
        }
      });
    };

    this.observer = new IntersectionObserver(cb, {});
    document
      .querySelectorAll("[data-header-target]")
      .forEach((target) => this.observer.observe(target));
  }

  _moveStickTarget() {
    // Moving this element in editor messes things up (sections disappear when you change things)
    // so just skip.
    if (window.Shopify?.designMode) return;

    // Move stick target before header (but after promo bar, if present).
    // We can't directly place the target there because these are both
    // sections in a section group. Only ever needs doing once — re-running it
    // on a later reconnect would shuffle the target again.
    if (this.stickTargetMoved) return;

    const target = document.querySelector('[data-header-target="stick"]');
    const header = this.closest(".shopify-section--header");

    if (!target || !header?.parentNode) return;

    header.parentNode.insertBefore(target, header);
    this.stickTargetMoved = true;
  }

  // The section wrapper is sticky, so anything rendered inside it sticks too. The quick-links
  // bar is meant to scroll away under the header, so lift it out to sit just after the
  // wrapper in normal flow.
  _moveQuickLinks() {
    const bar = this.parentNode?.querySelector(":scope > [data-quick-links]");
    const header = this.closest(".shopify-section--header");

    if (!bar || !header?.parentNode) return;

    // A theme-editor re-render rebuilds the section around a bar that was already lifted out.
    header.parentNode
      .querySelectorAll(":scope > [data-quick-links]")
      .forEach((stale) => stale.remove());

    header.parentNode.insertBefore(bar, header.nextSibling);
  }
}

if (!customElements.get("sticky-header")) {
  customElements.define("sticky-header", StickyHeader);
}
